import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { L10nLocale, L10N_LOCALE } from 'angular-l10n';
import { MetadataService, MetadataViewModel } from 'src/app/boxwood-api';
import { IncidentCategory, IncidentEvent, IncidentType } from 'src/app/hickory-api';
import { IncidentModelHelper } from 'src/app/shared/incident';

export interface IncidentSelectTypeData {
  pageTitle?: string;
  selectMultiple?: boolean;
  selected?: IncidentType[];
}
@Component({
  selector: 'app-incident-select-type',
  templateUrl: './select-type.component.html',
  styleUrls: ['./select-type.component.css'],
  standalone: false
})
export class IncidentSelectTypeComponent implements OnInit {
  readonly EMERGENCY: string = 'emergency';
  readonly OPERATIONS: string = 'operations';
  readonly pageSize: number = 8;

  categoryClassification: string = this.EMERGENCY;
  metadataViewModel?: MetadataViewModel = null;
  loadErrorMetadata: boolean = false;
  loadedMetadata: boolean = false;

  emergencyCategories?: IncidentCategory[] = null;
  operationsCategories?: IncidentCategory[] = null;
  selectedCategory?: IncidentCategory = null;
  typesByCategory?: { [id: string] : IncidentType[]; } = null;
  categoryTypes?: IncidentType[] = null;
  selectedEvents: IncidentEvent[] = [];

  totalCategories: number = 0
  pageOfCategories?: IncidentCategory[] = null;
  pageOfTypes?: IncidentType[] = null;
  currentCategoryPage?: number = null;
  currentTypePage?: number = null;

  get hasCategories(): boolean {
    return Boolean(this.pageOfCategories && this.pageOfCategories.length > 0);
  }

  get hasTypes(): boolean {
    return Boolean(this.pageOfTypes && this.pageOfTypes.length > 0);
  }

  constructor(
    private dialogRef: MatDialogRef<IncidentSelectTypeComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IncidentSelectTypeData,
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private metadataApi: MetadataService
  ) {
    if (!this.data) {
      this.data = { };
    }
  }

  ngOnInit() {
    this.fetchMetadata();
  }

  resetComponent() {
    this.categoryClassification = this.EMERGENCY;
    this.loadErrorMetadata = false;
    this.loadedMetadata = false;
    this.currentCategoryPage = null;
    this.currentTypePage = null;
    this.selectedCategory = null;
    this.pageOfTypes = null;
    this.currentTypePage = null;
    this.categoryTypes = null;
    this.loadSelectedEvents();
  }

  fetchMetadata() {
    this.loadErrorMetadata = false;
    const self = this;
    this.metadataApi.getMetadata().subscribe(r => {
      self.metadataViewModel = r;
      this.organizeEvents();
      this.loadCategoryPage(1);
    }, e=> {
      this.loadErrorMetadata = true;
    });
  }

  organizeEvents() {
    const newEmergencyCategories: IncidentCategory[] = [];
    const newOperationsCategories: IncidentCategory[] = [];
    const newTypesByCategory: { [id: string] : IncidentType[]; } = { };

    // organize categories by classification
    if (this.metadataViewModel && this.metadataViewModel.categories) {
      this.metadataViewModel.categories.forEach(c => {
        const IncidentCategory: IncidentCategory = c;
        const classification = c.classification && c.classification.name ? c.classification.name.toLowerCase() : null;
        if (classification === this.OPERATIONS) {
          newOperationsCategories.push(IncidentCategory);
        } else {
          newEmergencyCategories.push(IncidentCategory);
        }
      });
    }
    
    // organize types by categoryId
    if (this.metadataViewModel && this.metadataViewModel.types) {
      this.metadataViewModel.types.forEach(t => {
        const categoryId = t.categoryId;
        const incidentType: IncidentType = IncidentModelHelper.convertIncidentType(t);
        const types = newTypesByCategory[categoryId] || [];
        types.push(incidentType);
        newTypesByCategory[categoryId] = types;
      });
    }

    this.emergencyCategories = newEmergencyCategories;
    this.operationsCategories = newOperationsCategories;
    this.typesByCategory = newTypesByCategory;
    this.categoryTypes = null;

    this.loadSelectedEvents();
  }

  loadCategoryPage(page: number) {
    const items = this.categoryClassification == this.EMERGENCY ? this.emergencyCategories : this.operationsCategories;
    if (!items) return;

    const nextPage: IncidentCategory[] = [];
    let endIndex = this.pageSize * page;
    const startIndex = endIndex - this.pageSize;
    if (startIndex < items.length) {
      if (endIndex > items.length) endIndex = items.length;
      for(let i = startIndex; i < endIndex; i++) {
        nextPage.push(items[i]);
      }
    }

    this.totalCategories = items.length
    this.pageOfCategories = nextPage;
    this.currentCategoryPage = page;
    this.loadedMetadata = true;
  }

  loadTypePage(page: number) {
    const items = this.categoryTypes;
    if (!items) return;

    const nextPage: IncidentType[] = [];
    let endIndex = this.pageSize * page;
    const startIndex = endIndex - this.pageSize;
    if (startIndex < items.length) {
      if (endIndex > items.length) endIndex = items.length;
      for(let i = startIndex; i < endIndex; i++) {
        nextPage.push(items[i]);
      }
    }

    this.pageOfTypes = nextPage;
    this.currentTypePage = page;
    this.loadedMetadata = true;
  }

  loadSelectedEvents() {
    this.selectedEvents = [];
    const preselected = this.data.selected || [];
    if (this.metadataViewModel) {
      preselected.forEach(t => {
        const typeSummary = this.metadataViewModel.types.find(s => s.id === t.id);
        const categorySummary = this.metadataViewModel.categories.find(s => s.id === typeSummary.categoryId);
        const incident: IncidentEvent = {
          incidentCategory: { id: typeSummary.categoryId, name: categorySummary?.name },
          incidentType: t,
        }
        this.selectedEvents.push(incident);
      })
    }
  }

  isTypeSelected(incidentType: IncidentType) {
    return this.selectedEvents.findIndex(e => incidentType?.id === e.incidentType?.id) !== -1;
  }

  onCategoryPageChange(page: number) {
    this.loadCategoryPage(page);
  }

  onTypePageChange(page: number) {
    this.loadTypePage(page);
  }

  onCategoryClassificationChange(newClassification: string) {
    this.categoryClassification = newClassification;
    this.selectedCategory = null;
    this.pageOfTypes = null;
    this.currentTypePage = null;
    this.categoryTypes = null;

    if (!this.data.selectMultiple) {
      this.loadSelectedEvents();
    }
    
    if (this.metadataViewModel) {
      this.loadCategoryPage(1);
      if (this.selectedCategory) {

      }
    }
  }

  onCategorySelect(newCategory: IncidentCategory) {
    this.selectedCategory = newCategory;
    this.pageOfTypes = null;
    this.currentTypePage = null;
    this.categoryTypes = null;

    if (!this.data.selectMultiple) {
      this.loadSelectedEvents();
    }

    if (this.typesByCategory) {
      this.categoryTypes = this.typesByCategory[newCategory.id];
      this.loadTypePage(1);
    }
  }

  onTypeSelect(newType: IncidentType) {
    if (!this.isTypeSelected(newType)) {

      const incident: IncidentEvent = {
        incidentCategory: this.selectedCategory,
        incidentType: newType,
      }

      this.selectedEvents.push(incident);
      if (!this.data.selectMultiple) {
        this.onDoneSelecting();
      }
    }
  }

  onClearType(clearedType: IncidentType) {
    this.selectedEvents = this.selectedEvents?.filter(e => e.incidentType?.id !== clearedType?.id);
  }

  onDoneSelecting() {
    const data: IncidentSelectTypeData = { selected: this.selectedEvents, }
    this.dialogRef.close(data);
  }

  onCancelClick() {
    this.dialogRef.close();
  }

}
